import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Banknote, Wallet } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiGet, apiPost } from "@/services/api";
import { formatPrice } from "@/lib/utils";

type Withdrawal = {
  id: string;
  amount: number;
  status: string;
  method: string | null;
  adminNote: string | null;
  createdAt: string;
};

export function VendorWithdrawalsPage() {
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [availableBalance, setAvailableBalance] = useState<number | null>(null);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Commercial Bank of Ethiopia (CBE)");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function load() {
    try {
      const [wRes, eRes] = await Promise.all([
        apiGet<{ withdrawals: Withdrawal[] }>("/api/vendors/me/withdrawals"),
        apiGet<{ availableBalance: number }>("/api/vendors/me/earnings"),
      ]);
      setWithdrawals(wRes?.withdrawals ?? []);
      if (typeof eRes?.availableBalance === "number") {
        setAvailableBalance(eRes.availableBalance);
      }
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    void load();
  }, []);

  async function request() {
    const numAmount = Number(amount);
    if (!numAmount || numAmount <= 0) {
      toast.error("Please enter a valid withdrawal amount");
      return;
    }
    setSubmitting(true);
    try {
      await apiPost("/api/vendors/me/withdrawals", { amount: numAmount, method, note });
      toast.success("Withdrawal request submitted successfully!");
      setAmount("");
      setNote("");
      void load();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not request withdrawal");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl font-black text-slate-900 dark:text-white">
          Payouts & Withdrawals
        </h1>
        <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
          Request funds withdrawal to your designated bank account or mobile wallet.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[400px_1fr] gap-4 sm:gap-6 items-start">
        {/* Request Form */}
        <div className="amazon-card p-4 sm:p-6 space-y-4">
          <div className="flex items-center gap-2 pb-2 border-b border-slate-100 dark:border-white/10">
            <Wallet className="h-5 w-5 text-[#087DB5]" />
            <h2 className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">
              Request a Withdrawal
            </h2>
          </div>

          {availableBalance !== null && (
            <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/70 p-3 dark:border-emerald-900/40 dark:bg-emerald-950/30">
              <span className="text-[10px] font-black uppercase text-emerald-700 dark:text-emerald-400">
                Available to Withdraw
              </span>
              <p className="text-lg font-black text-emerald-800 dark:text-emerald-200">
                {formatPrice(availableBalance)}
              </p>
            </div>
          )}

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                Amount (ETB) <span className="text-rose-500">*</span>
              </label>
              <input
                type="number"
                inputMode="decimal"
                placeholder="e.g. 5000"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                Payout Method / Bank Account <span className="text-rose-500">*</span>
              </label>
              <input
                placeholder="e.g. CBE Account: 1000..., Telebirr: 09..."
                value={method}
                onChange={(e) => setMethod(e.target.value)}
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 dark:text-slate-200">
                Note to Administrator (Optional)
              </label>
              <input
                placeholder="Any special remarks or reference"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                className="mt-1 h-10 w-full rounded-xl border border-slate-300 bg-slate-50/60 px-3.5 text-sm text-slate-900 outline-none transition focus:bg-white focus:border-[#087DB5] focus:ring-2 focus:ring-[#087DB5]/20 dark:border-white/10 dark:bg-slate-800 dark:text-white"
              />
            </div>

            <Button
              disabled={submitting}
              onClick={() => void request()}
              className="w-full h-10 mt-2 bg-[#087DB5] hover:bg-[#073B70] text-white font-bold text-xs sm:text-sm shadow-xs active:scale-95 transition"
            >
              {submitting ? "Submitting Request…" : "Submit Withdrawal Request"}
            </Button>
          </div>
        </div>

        {/* Withdrawal History */}
        <div className="amazon-card p-4 sm:p-6 space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-slate-100 dark:border-white/10">
            <h2 className="font-bold text-sm sm:text-base text-slate-900 dark:text-white">
              Withdrawal History
            </h2>
            <span className="text-xs text-slate-500">
              {withdrawals.length} request{withdrawals.length !== 1 ? "s" : ""}
            </span>
          </div>

          <div className="divide-y divide-slate-100 dark:divide-white/5">
            {withdrawals.length === 0 && (
              <div className="py-8 text-center text-xs text-slate-500">
                <Banknote className="mx-auto h-8 w-8 text-slate-300 dark:text-slate-600 mb-1.5" />
                No withdrawal requests made yet.
              </div>
            )}
            {withdrawals.map((w) => (
              <div
                key={w.id}
                className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 py-3.5"
              >
                <div className="min-w-0 flex-1">
                  <p className="text-base font-black text-slate-900 dark:text-white">
                    {formatPrice(w.amount)}
                  </p>
                  <p className="mt-0.5 text-xs text-slate-600 dark:text-slate-300 break-words">
                    {w.method ?? "Bank"} · {new Date(w.createdAt).toLocaleDateString()}
                  </p>
                  {w.adminNote && (
                    <p className="mt-1 text-xs text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/40 rounded p-1.5 inline-block">
                      Note: {w.adminNote}
                    </p>
                  )}
                </div>
                <div className="shrink-0 self-start sm:self-center">
                  <Badge
                    tone={
                      w.status === "COMPLETED"
                        ? "success"
                        : w.status === "REJECTED" || w.status === "FAILED"
                        ? "deal"
                        : "prime"
                    }
                  >
                    {w.status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

